import React from 'react';
import { Box, Typography, IconButton } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core/styles';
import { CloudUpload, Description, Close } from '@material-ui/icons';
import { useDropzone } from 'react-dropzone';
import prettyBytes from 'pretty-bytes';
import { UploadedFile } from '../stages/types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    uploadContainer: {
      width: '100%',
      boxSizing: 'border-box',
      border: `2px dashed ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(4),
      cursor: 'pointer',
      transition: 'border-color 0.2s ease',
      '&:hover': {
        borderColor: theme.palette.primary.main,
      },
    },
    uploadContainerActive: {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
    uploadIcon: {
      fontSize: 64,
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(2),
    },
    fileItem: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1.5),
      backgroundColor: theme.palette.grey[50],
      borderRadius: theme.shape.borderRadius,
      marginBottom: theme.spacing(1),
    },
    fileIcon: {
      marginRight: theme.spacing(1.5),
      color: theme.palette.primary.main,
    },
  })
);

interface FileUploadAreaProps {
  files: UploadedFile[];
  onFilesAdded: (files: UploadedFile[]) => void;
  onFileRemoved: (index: number) => void;
  hint?: string;
}

export function FileUploadArea({ files, onFilesAdded, onFileRemoved, hint }: FileUploadAreaProps) {
  const classes = useStyles();

  const onDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      const newFiles = acceptedFiles.map((file) => ({
        file,
        name: file.name,
        size: file.size,
      }));
      onFilesAdded(newFiles);
    },
    [onFilesAdded]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: [
      'application/pdf',
      'text/plain',
      'text/csv',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
    ],
  });

  return (
    <>
      <Box
        {...getRootProps()}
        className={`${classes.uploadContainer} ${isDragActive ? classes.uploadContainerActive : ''}`}
      >
        <input {...getInputProps()} />
        <CloudUpload className={classes.uploadIcon} />
        <Typography variant='h6' gutterBottom>
          Drop files here or click to upload
        </Typography>
        <Typography variant='body2' color='textSecondary'>
          {hint || 'Supports PDF, DOCX, TXT, CSV, and images (JPG, PNG)'}
        </Typography>
      </Box>

      {files.length > 0 && (
        <Box mt={3}>
          <Typography variant='subtitle2' gutterBottom>
            Uploaded Files ({files.length})
          </Typography>
          {files.map((file, index) => (
            <Box key={`${file.name}-${file.size}`} className={classes.fileItem}>
              <Description className={classes.fileIcon} />
              <Box flex={1}>
                <Typography variant='body2'>{file.name}</Typography>
                <Typography variant='caption' color='textSecondary'>
                  {prettyBytes(file.size)}
                </Typography>
              </Box>
              <IconButton size='small' onClick={() => onFileRemoved(index)}>
                <Close fontSize='small' />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </>
  );
}
